
'use client';

import LandingLayout from '@/components/LandingLayout';
import { restaurantsFaqs } from '@/data/industryFaqs';
import { useAuth } from '@/contexts/AuthContextNext';
import { trackButtonClick, trackPageView, trackPhoneClick } from '@/lib/analytics';
import {
  CalendarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentTextIcon,
  PhoneIcon,
  ShieldCheckIcon,
  SparklesIcon,
  StarIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useEffect, useState } from 'react';

export default function RestaurantLanding() {
  useEffect(() => {
    trackPageView('Restaurants Landing', undefined, { funnel_step: 'industry_landing' });
  }, []);

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { isAuthenticated } = useAuth();

  const painPoints = [
    {
      problem: "Phone ringing during dinner rush and you're stuck in the kitchen",
      impact: "$50-200+ per missed reservation"
    },
    {
      problem: "Answering 'Do you take reservations?' and 'What time do you close?' 100+ times daily",
      impact: "6-10 hours weekly on the same questions"
    },
    {
      problem: "Customers calling during peak hours and getting voicemail",
      impact: "They call your competitor instead"
    },
    {
      problem: "No-shows because you forget to send reminders or confirm reservations",
      impact: "10-20% revenue loss from empty tables"
    }
  ];

  const restaurantBenefits = [
    {
      title: "Never Miss a Reservation Call",
      description: "Your AI answers every call during rush. Takes reservation details, confirms availability, and sends reminders automatically.",
      icon: PhoneIcon,
      stat: "100%"
    },
    {
      title: "Save 6-10 Hours Weekly",
      description: "Stop repeating hours, directions, menu info, and policies. AI handles customer intake perfectly every time, 24/7.",
      icon: ClockIcon,
      stat: "6-10 hrs"
    },
    {
      title: "Automatic Reservation Booking",
      description: "Customers book tables directly into your system. Reduces back-and-forth calls and confirmation emails.",
      icon: CalendarIcon,
      stat: "Auto"
    },
    {
      title: "Reduce No-Shows by 40%+",
      description: "AI sends automated reminders and confirmations. Customers receive friendly texts/calls day-of to confirm.",
      icon: ShieldCheckIcon,
      stat: "40%+"
    },
    {
      title: "Handle Overflow Gracefully",
      description: "During peak times, AI manages call volume, puts customers on waitlist, and updates them automatically.",
      icon: SparklesIcon,
      stat: "Unlimited"
    },
    {
      title: "Smart Call Qualification",
      description: "AI separates reservation calls from special requests, catering inquiries, and feedback. You get pre-categorized information.",
      icon: DocumentTextIcon,
      stat: "Pre-sorted"
    }
  ];

  const scenarios = [
    {
      title: "Saturday Night During Peak Service",
      problem: "7 PM: Three calls come in. You're plating dishes. Phone rings. Customers get voicemail. Two call other restaurants. You lose $300+ in revenue from walk-ins who couldn't reach you.",
      solution: "With Greet: All three calls answered instantly. Reservations booked, walk-ins offered 7:45 PM slot, all customers thrilled. Every table filled."
    },
    {
      title: "The 25% No-Show Problem",
      problem: "Monday night: 10 reservations booked. 3 don't show up. You have empty tables, staff standing around, and lost revenue. No way to prevent it without calling 100 confirmations manually.",
      solution: "With Greet: Automatic reminder texts at 48 hours and 2 hours. Confirmation prompt day-of. No-show rate drops to 5%. Full dining room."
    },
    {
      title: "Catering Inquiry During Service",
      problem: "Friday 6 PM: Someone calls about catering. You're busy with customers. Call goes unanswered. They email your competitor who responds fast. You miss a $2,000+ catering gig.",
      solution: "With Greet: Catering inquiry captured, details collected, you call back after service prep. Lead is pre-qualified. 70% conversion rate on catering leads."
    }
  ];

  const testimonials = [
    {
      name: "Marco Rossi",
      business: "Rossi's Italian Kitchen",
      stars: 5,
      quote: "We were missing so many reservation calls during dinner service. Greet answers everything now. My staff isn't interrupted constantly, I'm not stressed about missed bookings, and we're fully booked most nights.",
      metric: "+35% reservation bookings"
    },
    {
      name: "Jessica Chen",
      business: "Golden Dragon Restaurant",
      stars: 5,
      quote: "No-shows used to be a nightmare-tables sitting empty and revenue wasted. The automatic reminders from Greet cut our no-show rate in half. Now I'm not chasing confirmations and tables are full.",
      metric: "50% fewer no-shows"
    },
    {
      name: "Tom Bradley",
      business: "The Farmhouse Restaurant Group",
      stars: 5,
      quote: "As a multi-location operator, having Greet handle calls at both locations 24/7 is a game-changer. I'm not paying staff to answer phones, bookings are automatic, and we never miss a customer.",
      metric: "Both locations fully booked"
    }
  ];

  return (
    <LandingLayout>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-red-900 via-slate-900 to-red-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 via-orange-500/10 to-yellow-500/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-red-500/20 to-orange-500/20 blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-4xl">
            <div className="mb-6 inline-flex items-center gap-3 rounded-full bg-red-500/20 border border-red-400/30 px-4 py-2 text-sm font-semibold text-red-100">
              <span className="h-2 w-2 rounded-full bg-red-400"></span>
              #1 AI Receptionist for Restaurants
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Never Miss a Reservation During Dinner Rush
            </h1>
            <p className="text-xl text-red-100 mb-8 max-w-2xl leading-relaxed">
              Your AI receptionist answers every call, books reservations, confirms appointments, and reduces no-shows by 40%+-while you focus on creating amazing meals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                href={isAuthenticated ? "/app" : "/register"}
                onClick={() => trackButtonClick(isAuthenticated ? "go_to_dashboard" : "start_free_trial", "restaurant_hero")}
                className="bg-red-500 hover:bg-red-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-block"
              >
                {isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
              </Link>
              <a
                href="tel:+14158773166"
                onClick={() => trackPhoneClick('+14158773166', 'restaurant_demo')}
                className="bg-white/10 border border-white text-white hover:bg-white/20 px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
              >
                <PhoneIcon className="h-5 w-5 mr-2" />
                See Live Demo: (415) 877-3166
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              The Restaurant's Reservation Crisis
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Missed calls. No-shows. Staff interruptions. Lost revenue. Every phone ring during service is stress you don't need.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {painPoints.map((point, index) => (
              <div key={index} className="border-l-4 border-red-500 pl-6 py-4">
                <div className="flex items-start gap-4">
                  <XCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-2">{point.problem}</h3>
                    <p className="text-red-600 font-semibold">{point.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Built for Restaurants: Here's What Changes
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your AI receptionist handles reservations, confirmations, and special requests while you focus on the kitchen and dining room.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {restaurantBenefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center mb-6 shadow-lg transition-transform duration-300 group-hover:scale-110">
                  <benefit.icon className="h-8 w-8 text-white transition-transform duration-300 group-hover:scale-125" />
                </div>
                <div className="inline-block px-4 py-1 bg-red-100 rounded-full mb-4 transition-all duration-300 group-hover:bg-red-200 group-hover:scale-105">
                  <span className="text-2xl font-bold text-red-900 transition-colors duration-300 group-hover:text-red-900">{benefit.stat}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 transition-colors duration-300 group-hover:text-red-600">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed transition-colors duration-300 group-hover:text-gray-700">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real Scenarios */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How Restaurants Actually Use Greet
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real scenarios. Real revenue. Real solutions.
            </p>
          </div>
          
          <div className="space-y-8">
            {scenarios.map((scenario, index) => (
              <div key={index} className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-8 border border-red-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{scenario.title}</h3>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-start gap-3 mb-3">
                      <XCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900">Without Greet</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{scenario.problem}</p>
                  </div>
                  
                  <div>
                    <div className="flex items-start gap-3 mb-3">
                      <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900">With Greet</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{scenario.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Restaurants
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real restaurants. Real results. Real revenue growth.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.stars)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current transition-colors duration-300 group-hover:text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed transition-colors duration-300 group-hover:text-gray-900">"{testimonial.quote}"</p>
                <div className="bg-red-100 rounded-lg px-4 py-3 mb-4 transition-colors duration-300 group-hover:bg-red-200">
                  <p className="text-red-900 font-semibold text-sm">{testimonial.metric}</p>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 transition-colors duration-300 group-hover:text-red-600">{testimonial.name}</div>
                  <div className="text-gray-600 text-sm transition-colors duration-300 group-hover:text-gray-700">{testimonial.business}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Questions from Restaurants
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about AI call handling for restaurants.
            </p>
          </div>
          
          <div className="space-y-4">
            {restaurantsFaqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDownIcon 
                    className={`h-5 w-5 text-gray-500 transform transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                <div className={`px-6 text-gray-600 border-t border-gray-200 bg-gray-50 transition-all duration-300 ${
                  openFaq === index ? 'pb-4 max-h-96' : 'pb-0 max-h-0'
                } overflow-hidden`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-red-600 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Stop Losing Revenue to Missed Reservations
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Your AI receptionist is ready to answer 24/7. Start your free trial today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              href={isAuthenticated ? "/app" : "/register"}
              onClick={() => trackButtonClick(isAuthenticated ? "go_to_dashboard" : "start_free_trial", "restaurant_cta")}
              className="bg-white text-red-600 hover:bg-gray-50 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              {isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
            </Link>
            <a
              href="tel:+14158773166"
              onClick={() => trackPhoneClick('+14158773166', 'restaurant_cta')}
              className="bg-transparent border border-white text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
            >
              <PhoneIcon className="h-5 w-5 mr-2" />
              Call Demo: (415) 877-3166
            </a>
          </div>
        </div>
      </section>
    </LandingLayout>
  );
}
